import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Button,
} from "@mui/material";
import type {
  AccreditationCalendarItem,
  AccreditationProgramLinks as ProgramLinksType,
  AccreditationProgramSection,
} from "@services/facilityCompliance";

/** Normalize accreditor URL: use standardsUrl fallback when url is missing or matches accreditor domain. */
function normalizeAccreditorUrl(
  url: string | null | undefined,
  programLinks?: ProgramLinksType | null
): string {
  const fallback = programLinks?.standardsUrl?.trim() || "#";
  if (!url || !url.trim()) return fallback;
  try {
    const u = url.trim().toLowerCase();
    if (u.includes("dnv.us")) return programLinks?.standardsUrl?.trim() || fallback;
  } catch {
    // ignore
  }
  return url;
}

const fontFamily = "Rethink Sans, sans-serif";

/** Build description link list from API/DB (programLinks.referenceLinks). No hardcoded phrases or URLs; when referenceLinks is missing or empty, no in-text phrase links are rendered. */
function buildDescriptionLinks(programLinks?: ProgramLinksType | null): Array<{ phrase: string; url: string }> {
  const raw = programLinks?.referenceLinks;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const links = raw
    .filter((e) => e?.phrase?.trim() && e?.url?.trim())
    .map((e) => ({ phrase: e.phrase.trim(), url: e.url.trim() }));
  return links.sort((a, b) => b.phrase.length - a.phrase.length);
}

/** Resolve URL for a reference phrase (e.g. "42 CFR Part 482" or "42 CFR Part 482 (Hospital CoPs)" -> regulationUrl). Exact match first; then match when reference phrase contains a known link phrase (longest match). */
function getUrlForReferencePhrase(
  phrase: string,
  descriptionLinks: Array<{ phrase: string; url: string }>
): string | null {
  const trimmed = phrase.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const exact = descriptionLinks.find((e) => lower === e.phrase.toLowerCase());
  if (exact) return exact.url;
  const contained = descriptionLinks
    .filter((e) => lower.includes(e.phrase.toLowerCase()))
    .sort((a, b) => b.phrase.length - a.phrase.length)[0];
  return contained?.url ?? null;
}

/** Render description text with phrase->url links (e.g. regulation, standards). */
function renderDescriptionWithLinks(
  text: string | null | undefined,
  descriptionLinks: Array<{ phrase: string; url: string }>,
  linkSx: React.CSSProperties
): React.ReactNode {
  if (!text || !text.trim()) return null;
  if (descriptionLinks.length === 0) return text.trim();
  const parts: React.ReactNode[] = [];
  let remaining = text.trim();
  let key = 0;
  while (remaining.length > 0) {
    let earliest = remaining.length;
    let match: { phrase: string; url: string } | null = null;
    for (const { phrase, url } of descriptionLinks) {
      const i = remaining.indexOf(phrase);
      if (i !== -1 && i < earliest) {
        earliest = i;
        match = { phrase, url };
      }
    }
    if (match) {
      if (earliest > 0) {
        parts.push(<React.Fragment key={key++}>{remaining.slice(0, earliest)}</React.Fragment>);
      }
      parts.push(
        <Link
          key={key++}
          href={match.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "0.83rem", color: "#086e86", fontFamily, ...linkSx }}
        >
          {match.phrase}
        </Link>
      );
      remaining = remaining.slice(earliest + match.phrase.length);
    } else {
      parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }
  }
  if (parts.length === 0) return null;
  return parts.length === 1 ? parts[0] : parts;
}

const ONGOING_FREQUENCIES = ["triennial", "annual"];

function normalizeFrequency(f?: string | null): string {
  return (f ?? "").toLowerCase().trim();
}

function parseItemCode(item?: string): { code: string | null; title: string } {
  if (!item) return { code: null, title: "" };
  const match = item.match(/^([A-Z0-9]{2,4})\s*[—–-]\s*(.+)$/i);
  if (match) return { code: match[1].toUpperCase(), title: match[2].trim() };
  return { code: null, title: item };
}

/** Display code for UI: e.g. POST + "Survey Reporting" -> "Post-Survey". */
function displayCode(code: string | null, title: string): string | null {
  if (!code) return null;
  if (code === "POST" && /survey\s+reporting/i.test(title)) return "Post-Survey";
  return code;
}

function getBadgeVariant(
  frequency: string
): "triennial" | "annual" | "one-time" | "post-survey" | "default" {
  const f = frequency.toLowerCase();
  if (f === "triennial") return "triennial";
  if (f === "annual") return "annual";
  if (f === "one-time") return "one-time";
  if (f === "post-survey") return "post-survey";
  return "default";
}

const badgeStyles: Record<
  "triennial" | "annual" | "one-time" | "post-survey" | "default",
  { bg: string; color: string }
> = {
  triennial: { bg: "#e0f2f2", color: "#065b70" },
  annual: { bg: "#cff7ff", color: "#065b70" },
  "one-time": { bg: "#f0f7f7", color: "#086e86" },
  "post-survey": { bg: "#e8f4f4", color: "#065b70" },
  default: { bg: "#f0f7f7", color: "#6b7280" },
};

export interface AccreditationCalendarViewProps {
  calendarItems: AccreditationCalendarItem[];
  revision?: string;
  effectiveDate?: string;
  showFooter?: boolean;
  /** Program-level links from API (regulation, standards, portal). Optional; when absent, in-text links and portal button may be hidden or use safe fallbacks. */
  programLinks?: ProgramLinksType | null;
}

export default function AccreditationCalendarView({
  calendarItems,
  revision,
  effectiveDate,
  showFooter = false,
  programLinks,
}: AccreditationCalendarViewProps) {
  const descriptionLinks = useMemo(
    () => buildDescriptionLinks(programLinks),
    [programLinks]
  );

  const { ongoing, process } = useMemo(() => {
    const ongoing: AccreditationCalendarItem[] = [];
    const process: AccreditationCalendarItem[] = [];
    for (const cal of calendarItems) {
      const freq = normalizeFrequency(cal.frequency);
      if (ONGOING_FREQUENCIES.includes(freq)) ongoing.push(cal);
      else process.push(cal);
    }
    return { ongoing, process };
  }, [calendarItems]);

  const renderTable = (items: AccreditationCalendarItem[]) => (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 2px 16px rgba(6, 91, 112, 0.07)",
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#0a829d" }}>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
              }}
            >
              Standard / Process
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "38%",
              }}
            >
              Description
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "24%",
              }}
            >
              References
            </TableCell>
            <TableCell
              align="center"
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "12%",
              }}
            >
              Frequency
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((cal, cidx) => {
            const { code, title: itemTitle } = parseItemCode(cal.item);
            const codeDisplay = displayCode(code, itemTitle);
            const freq = normalizeFrequency(cal.frequency);
            const variant = getBadgeVariant(freq);
            const style = badgeStyles[variant];
            const displayFreq = cal.frequency?.trim() || freq || "—";
            const referencePhrases = cal.citation?.title
              ? cal.citation.title.split(";").map((s) => s.trim()).filter(Boolean)
              : [];
            return (
              <TableRow
                key={cidx}
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                  "&:last-child": { borderBottom: "none" },
                  "&:hover": { backgroundColor: "#f8fdfe" },
                }}
              >
                <TableCell
                  sx={{
                    width: "26%",
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontFamily,
                  }}
                >
                  {codeDisplay && (
                    <Typography
                      component="div"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: "#086e86",
                        textTransform: "uppercase",
                        mb: 0.25,
                      }}
                    >
                      {codeDisplay}
                    </Typography>
                  )}
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.92rem",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily,
                    }}
                  >
                    {itemTitle || cal.item || "—"}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: "38%",
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontFamily,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.83rem",
                      color: "#4a6e6e",
                      lineHeight: 1.55,
                      fontFamily,
                    }}
                  >
                    {cal.scheduleDescription
                      ? renderDescriptionWithLinks(cal.scheduleDescription, descriptionLinks, {})
                      : "—"}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: "24%",
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontFamily,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                    {referencePhrases.length > 0
                      ? referencePhrases.map((phrase, pidx) => {
                          const url =
                            getUrlForReferencePhrase(phrase, descriptionLinks) ??
                            (cal.citation?.url ? normalizeAccreditorUrl(cal.citation.url, programLinks) : null);
                          return url ? (
                            <Link
                              key={pidx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: "0.8rem", color: "#086e86", fontFamily }}
                            >
                              {phrase}
                            </Link>
                          ) : (
                            <Typography key={pidx} component="span" sx={{ fontSize: "0.8rem", color: "#6b7280", fontFamily }}>
                              {phrase}
                            </Typography>
                          );
                        })
                      : cal.citation?.url
                        ? (
                            <Link
                              href={normalizeAccreditorUrl(cal.citation.url, programLinks)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: "0.8rem", color: "#086e86", fontFamily }}
                            >
                              {cal.citation.title || "Reference"}
                            </Link>
                          )
                        : "—"}
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: "12%",
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontFamily,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "20px",
                      fontSize: "0.73rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      backgroundColor: style.bg,
                      color: style.color,
                    }}
                  >
                    {displayFreq}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );

  const renderSectionLabel = (label: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#065b70",
          fontFamily,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
    </Box>
  );

  return (
    <Box sx={{ fontFamily }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: "2px solid #065b70",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "#6b7280",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontFamily,
            }}
          >
            Schedule / calendar — Compliance Overview
          </Typography>
        </Box>
        {programLinks?.portalUrl && (
          <Button
            component="a"
            href={programLinks.portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            sx={{
              mt: 1,
              fontFamily,
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              minWidth: "64px",
              height: "28px",
              px: 1.5,
              color: "#086E86",
              borderColor: "#5BBFD6",
              backgroundColor: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                borderColor: "#35AFC8",
                backgroundColor: "#F4FCFD",
                boxShadow: "none",
              },
            }}
          >
             Login
          </Button>
        )}
      </Box>

      {/* Ongoing & Recurring */}
      {ongoing.length > 0 && (
        <>
          {renderSectionLabel("Ongoing & Recurring Requirements")}
          {renderTable(ongoing)}
        </>
      )}

      {/* Process & Event-Driven */}
      {process.length > 0 && (
        <>
          {renderSectionLabel("Process & Event-Driven Requirements")}
          {renderTable(process)}
        </>
      )}

      {/* Key */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          mt: ongoing.length > 0 || process.length > 0 ? -1.5 : 0,
          mb: 3,
        }}
      >
        {(["triennial", "annual", "one-time", "post-survey"] as const).map(
          (f) => {
            const style = badgeStyles[f];
            const labels: Record<typeof f, string> = {
              triennial: "Every 3 years",
              annual: "Every year",
              "one-time": "Initial only",
              "post-survey": "After each survey",
            };
            return (
              <Box
                key={f}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: "0.78rem",
                  color: "#6b7280",
                  fontFamily,
                }}
                component="span"
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 1.25,
                    py: 0.5,
                    borderRadius: "20px",
                    fontSize: "0.73rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    backgroundColor: style.bg,
                    color: style.color,
                  }}
                >
                  {f === "triennial"
                    ? "Triennial"
                    : f === "annual"
                      ? "Annual"
                      : f === "one-time"
                        ? "One-Time"
                        : "Post-Survey"}
                </Box>
                {labels[f]}
              </Box>
            );
          }
        )}
      </Box>

      {/* Footer */}
      {showFooter && (revision || effectiveDate) && (
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#6b7280",
            pt: 2,
            borderTop: "1px solid #e0e0e0",
            fontFamily,
          }}
        >
          NIAHO DNV
          {revision && ` — Revision ${revision}`}
          {effectiveDate && `, effective ${effectiveDate}`}
          {" · unicomply.ai"}
        </Typography>
      )}
    </Box>
  );
}

/** Parse section title into optional code and display title (e.g. "QM — Quality Management System" -> { code: "QM", title: "Quality Management System (...)" }). */
function parseSectionTitle(title?: string | null): { code: string | null; title: string } {
  if (!title || !title.trim()) return { code: null, title: "" };
  const match = title.trim().match(/^([A-Z0-9]{2,4})\s*[—–-]\s*(.+)$/i);
  if (match) return { code: match[1].toUpperCase(), title: match[2].trim() };
  return { code: null, title: title.trim() };
}

export interface AccreditationSectionsViewProps {
  sections: AccreditationProgramSection[];
  programLinks?: ProgramLinksType | null;
}

/** Compliable table view for NIAHO program sections below the calendar. */
export function AccreditationSectionsView({
  sections,
  programLinks,
}: AccreditationSectionsViewProps) {
  const descriptionLinks = useMemo(
    () => buildDescriptionLinks(programLinks),
    [programLinks]
  );

  if (!sections?.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 2px 16px rgba(6, 91, 112, 0.07)",
        overflow: "hidden",
        fontFamily,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#0a829d" }}>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "22%",
              }}
            >
              Standard / Process
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "42%",
              }}
            >
              Description
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "22%",
              }}
            >
              References
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                px: 2,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.85)",
                fontFamily,
                width: "14%",
              }}
            >
              Frequency
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map((sec, idx) => {
            const { code, title: sectionTitle } = parseSectionTitle(sec.title ?? sec.id ?? "");
            const codeDisplay = displayCode(code, sectionTitle);
            const titleDisplay = sectionTitle || sec.title || sec.id || `Section ${idx + 1}`;
            const hasCitation = !!(sec.citation?.title?.trim() || sec.citation?.url);
            const frequencyLine = [sec.frequency, sec.scheduleDescription].filter(Boolean).join(" · ");

            return (
              <TableRow
                key={sec.id ?? idx}
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                  "&:last-child": { borderBottom: "none" },
                  "&:hover": { backgroundColor: "#f8fdfe" },
                }}
              >
                <TableCell
                  sx={{
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontFamily,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.92rem",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily,
                    }}
                  >
                    {codeDisplay && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          color: "#086e86",
                          textTransform: "uppercase",
                          display: "block",
                          mb: 0.25,
                        }}
                      >
                        {codeDisplay}
                      </Typography>
                    )}
                    {titleDisplay}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontSize: "0.83rem",
                    color: "#4a6e6e",
                    lineHeight: 1.55,
                    fontFamily,
                  }}
                >
                  {sec.content?.trim() ? (
                    <Typography component="span" sx={{ whiteSpace: "pre-wrap" }}>
                      {renderDescriptionWithLinks(sec.content, descriptionLinks, {})}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontSize: "0.8rem",
                    color: "#6b7280",
                    fontFamily,
                  }}
                >
                  {hasCitation ? (
                    (() => {
                      const refTitle = sec.citation?.title?.trim();
                      if (!refTitle) {
                        return sec.citation?.url ? (
                          <Link
                            href={normalizeAccreditorUrl(sec.citation.url, programLinks)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "0.8rem", color: "#086e86", fontFamily }}
                          >
                            {sec.citation.title || "Source"}
                          </Link>
                        ) : null;
                      }
                      const referencePhrases = refTitle.split(";").map((s) => s.trim()).filter(Boolean);
                      return referencePhrases.length > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                          {referencePhrases.map((phrase, pidx) => {
                            const url =
                              getUrlForReferencePhrase(phrase, descriptionLinks) ??
                              (sec.citation?.url ? normalizeAccreditorUrl(sec.citation.url, programLinks) : null);
                            return url ? (
                              <Link
                                key={pidx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: "0.8rem", color: "#086e86", fontFamily }}
                              >
                                {phrase}
                              </Link>
                            ) : (
                              <Typography key={pidx} component="span" sx={{ fontSize: "0.8rem", color: "#6b7280", fontFamily }}>
                                {phrase}
                              </Typography>
                            );
                          })}
                        </Box>
                      ) : (
                        sec.citation?.url ? (
                          <Link
                            href={normalizeAccreditorUrl(sec.citation.url, programLinks)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "0.8rem", color: "#086e86", fontFamily }}
                          >
                            {refTitle}
                          </Link>
                        ) : (
                          <Typography component="span" sx={{ fontSize: "0.8rem", color: "#6b7280", fontFamily }}>
                            {refTitle}
                          </Typography>
                        )
                      );
                    })()
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    px: 2,
                    verticalAlign: "top",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    fontFamily,
                  }}
                >
                  {frequencyLine || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
